import React, { useMemo, useState } from "react";
import { Alert, Button, Card, DatePicker, Empty, Input, Modal, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, message } from "antd";
import { EditOutlined, FileExcelOutlined, IdcardOutlined, PrinterOutlined, ReloadOutlined, StopOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import ExcelExport from "components/shared-components/ExcelExport";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { PICKER_REPORT_MAX_DAYS, WAVE_STATUS } from "constants/PickingConstant";
import { usePickerReport, usePickers } from "data/hooks";
import { addDays } from "functions/pickDay";
import { buildPickerReportSheets, formatDuration, summarisePickers, totalPickerRows } from "functions/pickerStats";
import { toDayjs, todayDate } from "../shared";
import BadgePrintModal from "./BadgePrintModal";

const WAVE_STATUS_COLORS = {
  [WAVE_STATUS.OPEN]: "default",
  [WAVE_STATUS.BUSY]: "processing",
  [WAVE_STATUS.DONE]: "success",
  [WAVE_STATUS.CANCELLED]: "error",
};
const ORDER_STATUS_COLORS = { queued: "blue", picking: "processing", picked: "green", partial: "orange", aborted: "red" };

const TAB_PICKERS = "pickers";
const TAB_ROUNDS = "rounds";

const muted = (text) => <span style={{ color: "#bfbfbf" }}>{text}</span>;
const clock = (ms) => (ms ? dayjs(ms).format("HH:mm") : "-");
const perHour = (value) => (value === null || value === undefined ? "-" : Math.round(value));
const byNumber = (key) => (a, b) => (a[key] || 0) - (b[key] || 0);

/**
 * Orderpicking -> Pickers: who has which badge, and what everyone picked in a period - per picker
 * on one tab, per round (and per order in it) on the other. The badge table is also the register
 * (data/hooks/usePickers); the numbers come from the pick events (functions/pickerStats), so they
 * are there as soon as a scanner has synced, processed onto the orders or not. The period and the
 * tab live in the URL (`?from=…&to=…&tab=rounds`).
 */
const Pickers = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const to = params.get("to") || todayDate();
  const from = params.get("from") || addDays(to, -6);
  const tab = params.get("tab") === TAB_ROUNDS ? TAB_ROUNDS : TAB_PICKERS;

  const { pickers, error: registerError, isLoading: registerLoading, nameOf, registerBadges, updatePicker } = usePickers();
  const report = usePickerReport(from, to);
  const [showInactive, setShowInactive] = useState(false);
  const [badgeModal, setBadgeModal] = useState({ open: false, text: "" });
  const [editing, setEditing] = useState(null);
  const [busyCode, setBusyCode] = useState(null);

  const rows = useMemo(() => summarisePickers(report.waves, pickers), [report.waves, pickers]);
  const visibleRows = useMemo(() => rows.filter((row) => showInactive || row.active || row.hasActivity), [rows, showInactive]);
  const totals = useMemo(() => totalPickerRows(visibleRows), [visibleRows]);
  const sheets = useMemo(() => buildPickerReportSheets({ pickerRows: visibleRows, waves: report.waves, nameOf }), [visibleRows, report.waves, nameOf]);

  const setPeriod = (range) => {
    const merged = new URLSearchParams(location.search);
    if (range && range[0] && range[1]) {
      merged.set("from", range[0].format(DATE_FORMAT_DD_MM_YYYY));
      merged.set("to", range[1].format(DATE_FORMAT_DD_MM_YYYY));
    }
    navigate(`${urls.pickers}?${merged.toString()}`, { replace: true });
  };

  const setTab = (key) => {
    const merged = new URLSearchParams(location.search);
    if (key === TAB_ROUNDS) merged.set("tab", TAB_ROUNDS);
    else merged.delete("tab");
    navigate(`${urls.pickers}?${merged.toString()}`, { replace: true });
  };

  const who = (code) => (code ? nameOf(code) || code : "-");

  const toggleActive = async (row) => {
    setBusyCode(row.code);
    try {
      await updatePicker(row.code, { active: !row.active });
      message.success(t(row.active ? "orderpicking.pickers.switched_off" : "orderpicking.pickers.switched_on", { code: row.code }), 4);
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setBusyCode(null);
    }
  };

  const saveName = async () => {
    setBusyCode(editing.code);
    try {
      await updatePicker(editing.code, { name: editing.name });
      message.success(t("orderpicking.pickers.saved"), 3);
      setEditing(null);
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setBusyCode(null);
    }
  };

  const pickerColumns = [
    {
      title: t("orderpicking.pickers.col_badge"),
      dataIndex: "code",
      width: 190,
      render: (code, row) => (
        <Space size={4} wrap>
          <Typography.Text code style={{ whiteSpace: "nowrap" }}>
            {code}
          </Typography.Text>
          {!row.registered && <Tag color="orange">{t("orderpicking.pickers.not_registered")}</Tag>}
          {row.registered && !row.active && <Tag>{t("orderpicking.pickers.inactive")}</Tag>}
        </Space>
      ),
    },
    {
      title: t("orderpicking.pickers.col_name"),
      dataIndex: "name",
      render: (name) => (name ? <strong>{name}</strong> : muted("-")),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || "", "nl"),
    },
    { title: t("orderpicking.pickers.col_rounds"), dataIndex: "rounds", width: 80, sorter: byNumber("rounds") },
    {
      title: t("orderpicking.pickers.col_orders"),
      dataIndex: "orders",
      width: 130,
      sorter: byNumber("orders"),
      render: (orders, row) => (
        <Space size={4} wrap>
          <span>{orders}</span>
          {row.partialOrders > 0 && <Tag color="orange">{t("orderpicking.pickers.partial", { count: row.partialOrders })}</Tag>}
        </Space>
      ),
    },
    { title: t("orderpicking.pickers.col_lines"), dataIndex: "linesPicked", width: 80, sorter: byNumber("linesPicked") },
    { title: t("orderpicking.pickers.col_units"), dataIndex: "units", width: 80, sorter: byNumber("units") },
    { title: t("orderpicking.pickers.col_work"), dataIndex: "workMs", width: 110, sorter: byNumber("workMs"), render: formatDuration },
    { title: t("orderpicking.pickers.col_per_order"), dataIndex: "avgOrderMs", width: 110, sorter: byNumber("avgOrderMs"), render: formatDuration },
    { title: t("orderpicking.pickers.col_per_hour"), dataIndex: "unitsPerHour", width: 110, sorter: byNumber("unitsPerHour"), render: perHour },
    { title: t("orderpicking.pickers.col_empty"), dataIndex: "emptyLocations", width: 100, sorter: byNumber("emptyLocations") },
    { title: t("orderpicking.pickers.col_manual"), dataIndex: "manualUnits", width: 100, sorter: byNumber("manualUnits") },
    { title: t("orderpicking.pickers.col_aborted"), dataIndex: "abortedOrders", width: 100, sorter: byNumber("abortedOrders") },
    {
      title: "",
      dataIndex: "actions",
      width: 120,
      render: (_, row) =>
        row.registered && !registerError ? (
          <Space size={4}>
            <Tooltip title={t("orderpicking.pickers.edit")}>
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditing({ code: row.code, name: row.name || "" })} />
            </Tooltip>
            <Tooltip title={t("orderpicking.pickers.reprint")}>
              <Button size="small" icon={<PrinterOutlined />} onClick={() => setBadgeModal({ open: true, text: row.code })} />
            </Tooltip>
            <Tooltip title={t(row.active ? "orderpicking.pickers.switch_off" : "orderpicking.pickers.switch_on")}>
              <Button size="small" icon={row.active ? <StopOutlined /> : <UndoOutlined />} loading={busyCode === row.code} onClick={() => toggleActive(row)} />
            </Tooltip>
          </Space>
        ) : null,
    },
  ];

  const orderColumns = [
    { title: "#", dataIndex: "sequence", width: 50 },
    { title: t("orderpicking.pickers.col_order"), dataIndex: "orderId" },
    {
      title: t("orderpicking.pickers.col_shop"),
      dataIndex: "shopSource",
      width: 70,
      render: (shop) => <Tag color={shop === "VCE" ? "green" : "purple"}>{shop}</Tag>,
    },
    { title: t("orderpicking.pickers.col_pickers"), dataIndex: "pickerId", render: who },
    {
      title: t("orderpicking.pickers.col_status"),
      dataIndex: "status",
      render: (status) => <Tag color={ORDER_STATUS_COLORS[status] || "default"}>{t(`order.picking_${status}`)}</Tag>,
    },
    { title: t("orderpicking.pickers.col_lines"), dataIndex: "linesPicked", render: (value, order) => `${value} / ${order.lines}` },
    { title: t("orderpicking.pickers.col_units"), dataIndex: "units", render: (value, order) => `${value} / ${order.unitsExpected}` },
    { title: t("orderpicking.pickers.col_time"), dataIndex: "workMs", render: (ms, order) => (order.startedAt ? formatDuration(ms) : muted("-")) },
    { title: t("orderpicking.pickers.col_empty"), dataIndex: "emptyLocations" },
    { title: t("orderpicking.pickers.col_manual"), dataIndex: "manualUnits" },
  ];

  const waveColumns = [
    { title: t("orderpicking.pickers.col_pick_day"), dataIndex: "pickDay", width: 110 },
    {
      title: t("orderpicking.pickers.col_round"),
      dataIndex: "waveNumber",
      width: 90,
      render: (number, wave) => (
        <Link to={`${urls.pickWaves}?pickDay=${wave.pickDay}&wave=${wave.waveId}`}>{t("orderpicking.waves.round_number", { number })}</Link>
      ),
    },
    {
      title: t("orderpicking.pickers.col_status"),
      dataIndex: "status",
      width: 140,
      render: (status) => <Tag color={WAVE_STATUS_COLORS[status] || "default"}>{t(`orderpicking.waves.status_${status}`)}</Tag>,
    },
    { title: t("orderpicking.pickers.col_pickers"), dataIndex: "pickers", render: (codes) => (codes.length ? codes.map(who).join(", ") : muted("-")) },
    { title: t("orderpicking.pickers.col_orders"), dataIndex: "closedCount", width: 90, render: (closed, wave) => `${closed} / ${wave.orderCount}` },
    { title: t("orderpicking.pickers.col_units"), dataIndex: "units", width: 80 },
    { title: t("orderpicking.pickers.col_start"), dataIndex: "startedAt", width: 80, render: clock },
    { title: t("orderpicking.pickers.col_end"), dataIndex: "endedAt", width: 80, render: clock },
    { title: t("orderpicking.pickers.col_work"), dataIndex: "workMs", width: 110, render: formatDuration },
    {
      title: t("orderpicking.pickers.col_interrupted"),
      dataIndex: "pauseMs",
      width: 120,
      render: (pauseMs, wave) =>
        pauseMs + wave.idleMs > 0 ? (
          <Tooltip title={t("orderpicking.pickers.interrupted_help", { pause: formatDuration(pauseMs), idle: formatDuration(wave.idleMs) })}>
            <span>{formatDuration(pauseMs + wave.idleMs)}</span>
          </Tooltip>
        ) : (
          muted("-")
        ),
    },
    { title: t("orderpicking.pickers.col_per_order"), dataIndex: "avgOrderMs", width: 110, render: formatDuration },
  ];

  return (
    <Card
      title={t("orderpicking.pickers.title")}
      extra={
        <Space wrap>
          <DatePicker.RangePicker
            value={[toDayjs(from), toDayjs(to)]}
            locale={locale}
            format={DATE_FORMAT_DD_MM_YYYY}
            allowClear={false}
            onChange={setPeriod}
          />
          <Tooltip title={registerError ? t("orderpicking.pickers.register_error") : null}>
            <Button icon={<IdcardOutlined />} disabled={!!registerError || registerLoading} onClick={() => setBadgeModal({ open: true, text: "" })}>
              {t("orderpicking.pickers.print_badges")}
            </Button>
          </Tooltip>
          <ExcelExport data={sheets} fileName={`pickers ${from} tot ${to}`}>
            <Button icon={<FileExcelOutlined />} disabled={report.isLoading || !!report.error}>
              {t("orderpicking.pickers.export")}
            </Button>
          </ExcelExport>
          <Button icon={<ReloadOutlined />} onClick={() => report.refresh()}>
            {t("orderpicking.pickers.refresh")}
          </Button>
        </Space>
      }
    >
      <p style={{ maxWidth: 860, color: "#6b6b6b" }}>{t("orderpicking.pickers.explanation")}</p>
      {!!registerError && (
        <Alert type="warning" showIcon className="mb-3" message={t("orderpicking.pickers.register_error")} description={t("orderpicking.pickers.register_error_help")} />
      )}
      {report.tooLong && <Alert type="info" showIcon className="mb-3" message={t("orderpicking.pickers.too_long", { max: PICKER_REPORT_MAX_DAYS })} />}
      {!!report.error && <Alert type="error" showIcon className="mb-3" message={t("orderpicking.pickers.error")} description={report.error.message} />}

      {report.isLoading ? (
        <Loading />
      ) : (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          tabBarExtraContent={
            tab === TAB_PICKERS ? (
              <Space size={8}>
                <Switch size="small" checked={showInactive} onChange={setShowInactive} />
                <span>{t("orderpicking.pickers.show_inactive")}</span>
              </Space>
            ) : null
          }
          items={[
            {
              key: TAB_PICKERS,
              label: t("orderpicking.pickers.tab_pickers", { count: visibleRows.length }),
              children: (
                <div className="table-responsive">
                  <Table
                    size="small"
                    rowKey="code"
                    columns={pickerColumns}
                    dataSource={visibleRows}
                    pagination={false}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orderpicking.pickers.no_register")} /> }}
                    summary={() =>
                      visibleRows.length > 1 ? (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <strong>{t("orderpicking.pickers.total")}</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}>{totals.orders}</Table.Summary.Cell>
                          <Table.Summary.Cell index={4}>{totals.linesPicked}</Table.Summary.Cell>
                          <Table.Summary.Cell index={5}>{totals.units}</Table.Summary.Cell>
                          <Table.Summary.Cell index={6}>{formatDuration(totals.workMs)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={7}>{formatDuration(totals.avgOrderMs)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={8}>{perHour(totals.unitsPerHour)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={9}>{totals.emptyLocations}</Table.Summary.Cell>
                          <Table.Summary.Cell index={10}>{totals.manualUnits}</Table.Summary.Cell>
                          <Table.Summary.Cell index={11}>{totals.abortedOrders}</Table.Summary.Cell>
                          <Table.Summary.Cell index={12} />
                        </Table.Summary.Row>
                      ) : null
                    }
                  />
                </div>
              ),
            },
            {
              key: TAB_ROUNDS,
              label: t("orderpicking.pickers.tab_rounds", { count: report.waves.length }),
              children: (
                <div className="table-responsive">
                  <Table
                    size="small"
                    rowKey="waveId"
                    columns={waveColumns}
                    dataSource={report.waves}
                    pagination={false}
                    expandable={{
                      expandedRowRender: (wave) => (
                        <Table size="small" rowKey="orderRef" columns={orderColumns} dataSource={wave.orders} pagination={false} />
                      ),
                    }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orderpicking.pickers.empty")} /> }}
                  />
                </div>
              ),
            },
          ]}
        />
      )}

      <BadgePrintModal
        open={badgeModal.open}
        initialText={badgeModal.text}
        pickers={pickers}
        registerError={registerError}
        registerBadges={registerBadges}
        onClose={() => setBadgeModal({ open: false, text: "" })}
      />
      <Modal
        open={!!editing}
        title={editing ? t("orderpicking.pickers.edit_title", { code: editing.code }) : ""}
        okText={t("orderpicking.pickers.saved_button")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !editing || !editing.name.trim(), loading: !!editing && busyCode === editing.code }}
        onCancel={() => setEditing(null)}
        onOk={saveName}
      >
        <p style={{ color: "#6b6b6b" }}>{t("orderpicking.pickers.edit_help")}</p>
        {!!editing && <Input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} onPressEnter={() => editing.name.trim() && saveName()} />}
      </Modal>
    </Card>
  );
};

export default Pickers;
